import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';
import { PROGRAM_PREVIEW_MAX_LENGTH } from '../constants';

interface ManualEditModalProps {
  open: boolean;
  currentText: string;
  onClose: () => void;
  onSave: (text: string) => void;
}

const ManualEditModal: React.FC<ManualEditModalProps> = ({ open, currentText, onClose, onSave }) => {
  const [text, setText] = useState(currentText);

  useEffect(() => {
    if (open) setText(currentText);
  }, [open, currentText]);

  const overLimit = text.length > PROGRAM_PREVIEW_MAX_LENGTH;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          p: 3
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
          Make manual edits
        </Typography>
        <Box component="button" onClick={onClose} sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5 }}>
          <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 20 }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
          Edit description:
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: overLimit ? 'var(--UI-Error, #F44336)' : 'var(--Text-text-tertiary, #868686)'
          }}
        >
          {text.length}/{PROGRAM_PREVIEW_MAX_LENGTH}
        </Typography>
      </Box>

      <Box
        component="textarea"
        value={text}
        maxLength={PROGRAM_PREVIEW_MAX_LENGTH}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
        sx={{
          width: '100%',
          minHeight: 220,
          background: '#0F0F0F',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          borderRadius: '8px',
          p: 2,
          color: 'var(--Text-text-primary, #FDFDFD)',
          fontFamily: 'Inter',
          fontSize: 14,
          lineHeight: '22px',
          resize: 'vertical',
          outline: 'none',
          '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{ ...secondaryButtonSx, cursor: 'pointer' }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={() => onSave(text)}
          disabled={overLimit}
          sx={{ ...ctaButtonSx, border: 'none', cursor: overLimit ? 'not-allowed' : 'pointer' }}
        >
          Save Changes
        </Box>
      </Box>
    </Dialog>
  );
};

export default ManualEditModal;
